// src/features/my-shift/components/MonthGrid.tsx
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { eachDayOfInterval, format, isSameMonth } from "date-fns";
import { todayInTimezone } from "@/features/shift/today";
import { formatLongDate } from "@/i18n/dateFormat";
import type { Shift } from "@/features/shift/types";
import { getShiftStartEnd } from "@/features/shift/types";
import { useShiftTypeLabel } from "@/features/shift/useShiftTypeLabel";
import { shiftColor } from "../shiftColors";
import { getMonthRange, formatTime12 } from "../calendar";
import { cn } from "@/lib/utils";

interface Props {
  anchor: Date;
  byDate: Record<string, Shift>;
  pendingDates: Set<string>;
  selectedDate: string | null;
  timezone: string;
  onSelectDate: (date: string) => void;
}

// 周一为首列，需与 calendar.ts 的 WEEK_OPTS.weekStartsOn(=1) 保持一致
const WEEKDAY_KEYS = ["mon", "tue", "wed", "thu", "fri", "sat", "sun"] as const;

export function MonthGrid({ anchor, byDate, pendingDates, selectedDate, timezone, onSelectDate }: Props) {
  const { t, i18n } = useTranslation('shifts')
  const shiftTypeLabel = useShiftTypeLabel();
  // 「今天」「过去」按用户时区的日历日比（网格日期是 yyyy-MM-dd 标签，不按浏览器时区取今天）
  const { days, todayStr } = useMemo(() => {
    const { start, end } = getMonthRange(anchor);
    const days = eachDayOfInterval({ start: new Date(`${start}T12:00:00`), end: new Date(`${end}T12:00:00`) });
    return { days, todayStr: todayInTimezone(timezone) };
  }, [anchor, timezone]);

  return (
    <div>
      <div className="mb-1.5 grid grid-cols-7">
        {WEEKDAY_KEYS.map((w) => (
          <div key={w} className="py-1 text-center text-[10px] font-bold uppercase text-muted-foreground">{t(`myShift.monthGrid.weekdays.${w}`)}</div>
        ))}
      </div>
      <div className="grid grid-cols-7 gap-1.5">
        {days.map((d) => {
          const dateStr = format(d, "yyyy-MM-dd");
          const inMonth = isSameMonth(d, anchor);
          const past = dateStr < todayStr;
          const today = dateStr === todayStr;
          const selected = selectedDate === dateStr;
          const sch = byDate[dateStr];
          const code = sch?.shiftTypeCode || sch?.shift_type_code;
          const { startAt, endAt } = sch ? getShiftStartEnd(sch) : { startAt: "", endAt: "" };
          const color = shiftColor(code);
          const hasTime = code !== "off" && !!startAt && !!endAt;

          return (
            <button
              type="button" key={dateStr} onClick={() => onSelectDate(dateStr)}
              aria-label={formatLongDate(d, i18n.language)}
              aria-pressed={selected}
              className={cn(
                "min-h-[80px] rounded-lg border border-border bg-card p-1.5 text-left transition-colors hover:border-purple-300",
                past && "bg-muted",
                !inMonth && "bg-muted opacity-50",
                today && "border-purple-600 ring-1 ring-purple-600",
                selected && "border-purple-600 ring-2 ring-purple-600",
              )}
            >
              <div className="flex items-center justify-between">
                <span className={cn("text-xs font-bold", today ? "text-purple-700" : past ? "text-slate-300" : "text-muted-foreground")}>
                  {format(d, "d")}
                </span>
                {pendingDates.has(dateStr) && <span className="text-[10px] text-amber-500">⚡</span>}
              </div>
              {sch && (
                <div
                  className="mt-1.5 rounded border-l-[3px] px-1.5 py-1 text-[10.5px] font-semibold"
                  style={{ background: color.bg, color: color.text, borderColor: color.border }}
                >
                  <span>{hasTime ? (code ? shiftTypeLabel(code) : t('myShift.monthGrid.shift')) : t('myShift.monthGrid.off')}</span>
                  {hasTime && (
                    <span className="block text-[9px] font-medium opacity-80">
                      {formatTime12(startAt, timezone, i18n.language)}–{formatTime12(endAt, timezone, i18n.language)}
                    </span>
                  )}
                </div>
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
}
